import * as React from "react";
import { useCallback, useContext, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import isEqual from "lodash/isEqual";
import { widgetDefinition } from "views/logic/Widgets";
import WidgetFocusContext from "views/components/contexts/WidgetFocusContext";
import TimerangeInfo from "views/components/widgets/TimerangeInfo";
import useWidgetResults from "views/components/useWidgetResults";
import useActiveQueryId from "views/hooks/useActiveQueryId";
import useViewsDispatch from "views/stores/useViewsDispatch";
import { updateWidget, updateWidgetConfig, setWidgetTitle, updateDescription } from "views/logic/slices/widgetActions";
import useAutoRefresh from "views/hooks/useAutoRefresh";
import useViewType from "views/hooks/useViewType";
import View from "views/logic/views/View";
import IfDashboard from "views/components/dashboard/IfDashboard";
import FullSizeContainer from "views/components/aggregationbuilder/FullSizeContainer";
import {
  useSendWidgetEditTelemetry,
  useSendWidgetEditCancelTelemetry,
  useSendWidgetConfigUpdateTelemetry
} from "views/components/widgets/telemetry";
import TextOverflowEllipsis from "components/common/TextOverflowEllipsis";
import useGlobalOverride from "views/hooks/useGlobalOverride";
import { setGlobalOverrideTimerange, setGlobalOverrideQuery } from "views/logic/slices/searchExecutionSlice";
import useQueryFieldTypes from "views/hooks/useQueryFieldTypes";
import WidgetFrame from "./WidgetFrame";
import WidgetHeader from "./WidgetHeader";
import EditWidgetFrame from "./EditWidgetFrame";
import LoadingWidget from "./LoadingWidget";
import ErrorWidget from "./ErrorWidget";
import WidgetColorContext from "./WidgetColorContext";
import WidgetErrorBoundary from "./WidgetErrorBoundary";
import WidgetActionsMenu from "./WidgetActionsMenu";
import WidgetWarmTierAlert from "./WidgetWarmTierAlert";
import { useIsInteractiveMode } from "../contexts/InteractiveContext";
const _visualizationForType = (type) => widgetDefinition(type).visualizationComponent;
const _editComponentForType = (type) => widgetDefinition(type).editComponent;
const _hasOwnEditSubmitButton = (type) => widgetDefinition(type).hasEditSubmitButton;
const WidgetFooter = styled.div(
  ({ theme }) => css`
    font-size: ${theme.fonts.size.tiny};
    color: ${theme.colors.gray[30]};
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 10px;
  `
);
const Visualization = ({
  title,
  id,
  widget,
  fields,
  queryId,
  editing,
  setLoadingState,
  onToggleEdit,
  onWidgetConfigChange
}) => {
  const VisComponent = useMemo(() => _visualizationForType(widget.type), [widget.type]);
  const { error: errors, widgetData: data } = useWidgetResults(id);
  if (errors && errors.length > 0) {
    return /* @__PURE__ */ React.createElement(ErrorWidget, { errors });
  }
  if (data) {
    const { config, filter } = widget;
    return /* @__PURE__ */ React.createElement(FullSizeContainer, null, ({ height, width }) => /* @__PURE__ */ React.createElement(
      VisComponent,
      {
        config,
        data,
        editing,
        fields,
        filter,
        queryId,
        onConfigChange: onWidgetConfigChange,
        setLoadingState,
        title,
        toggleEdit: onToggleEdit,
        type: widget.type,
        id,
        height,
        width
      }
    ));
  }
  return /* @__PURE__ */ React.createElement(LoadingWidget, null);
};
export const EditWrapper = ({
  children,
  config,
  editing,
  fields,
  id,
  onToggleEdit,
  onCancelEdit,
  onWidgetConfigChange,
  type,
  showQueryControls = void 0
}) => {
  const EditComponent = useMemo(() => _editComponentForType(type), [type]);
  const hasOwnSubmitButton = _hasOwnEditSubmitButton(type);
  const dispatch = useViewsDispatch();
  const onSubmitEdit = useCallback(
    (newWidget, hasChanges) => {
      if (hasChanges) {
        return dispatch(updateWidget(newWidget.id, newWidget)).then(() => onToggleEdit());
      }
      onToggleEdit();
      return Promise.resolve();
    },
    [dispatch, onToggleEdit]
  );
  return editing ? /* @__PURE__ */ React.createElement(
    EditWidgetFrame,
    {
      onSubmit: onSubmitEdit,
      onCancel: onCancelEdit,
      displaySubmitActions: !hasOwnSubmitButton,
      showQueryControls
    },
    /* @__PURE__ */ React.createElement(
      EditComponent,
      {
        config,
        fields,
        editing,
        id,
        type,
        onCancel: onCancelEdit,
        onChange: onWidgetConfigChange
      },
      children
    )
  ) : children;
};
const WidgetDescription = ({ text }) => /* @__PURE__ */ React.createElement(TextOverflowEllipsis, null, text);
const useUndoChangesOnCancel = (id, editing, widget) => {
  const globalOverride = useGlobalOverride();
  const [previousState, setPreviousState] = useState(editing ? { widget, globalOverride } : void 0);
  const clearPreviousState = useCallback(() => setPreviousState(void 0), []);
  const { stopAutoRefresh } = useAutoRefresh();
  const dispatch = useViewsDispatch();
  const { setWidgetEditing, unsetWidgetEditing } = useContext(WidgetFocusContext);
  const sendWidgetEditTelemetry = useSendWidgetEditTelemetry();
  const sendWidgetEditCancelTelemetry = useSendWidgetEditCancelTelemetry();
  const onToggleEdit = useCallback(() => {
    if (editing) {
      unsetWidgetEditing();
      clearPreviousState();
    } else {
      sendWidgetEditTelemetry();
      stopAutoRefresh();
      setWidgetEditing(widget.id);
      setPreviousState({ widget, globalOverride });
    }
  }, [
    clearPreviousState,
    editing,
    globalOverride,
    sendWidgetEditTelemetry,
    setWidgetEditing,
    stopAutoRefresh,
    unsetWidgetEditing,
    widget
  ]);
  const onCancelEdit = useCallback(() => {
    var _a, _b;
    sendWidgetEditCancelTelemetry();
    if (previousState) {
      dispatch(updateWidget(id, previousState.widget));
      if (!isEqual((_a = previousState.globalOverride) == null ? void 0 : _a.timerange, globalOverride == null ? void 0 : globalOverride.timerange)) {
        dispatch(setGlobalOverrideTimerange(previousState.globalOverride.timerange));
      }
      if (!isEqual((_b = previousState.globalOverride) == null ? void 0 : _b.query, globalOverride == null ? void 0 : globalOverride.query)) {
        dispatch(setGlobalOverrideQuery(previousState.globalOverride.query.query_string));
      }
    }
    onToggleEdit();
  }, [
    dispatch,
    globalOverride == null ? void 0 : globalOverride.query,
    globalOverride == null ? void 0 : globalOverride.timerange,
    id,
    onToggleEdit,
    previousState,
    sendWidgetEditCancelTelemetry
  ]);
  return useMemo(() => ({ onCancelEdit, onToggleEdit }), [onCancelEdit, onToggleEdit]);
};
const Widget = ({ id, editing = false, widget, title, position, onPositionsChange }) => {
  const viewType = useViewType();
  const fields = useQueryFieldTypes();
  const [loading, setLoading] = useState(false);
  const { focusedWidget } = useContext(WidgetFocusContext);
  const dispatch = useViewsDispatch();
  const sendWidgetConfigUpdateTelemetry = useSendWidgetConfigUpdateTelemetry();
  const interactive = useIsInteractiveMode();
  const isDashboard = viewType === View.Type.Dashboard;
  const { onCancelEdit, onToggleEdit } = useUndoChangesOnCancel(id, editing, widget);
  const onRenameWidget = useCallback((newTitle) => dispatch(setWidgetTitle(id, newTitle)), [dispatch, id]);
  const onUpdateDescription = useCallback(
    (newDescription) => dispatch(updateDescription(widget, newDescription)),
    [dispatch, widget]
  );
  const onWidgetConfigChange = useCallback(
    async (newWidgetConfig) => {
      sendWidgetConfigUpdateTelemetry();
      return dispatch(updateWidgetConfig(id, newWidgetConfig)).then(() => {
      });
    },
    [dispatch, id, sendWidgetConfigUpdateTelemetry]
  );
  const activeQuery = useActiveQueryId();
  const { config } = widget;
  const isFocused = (focusedWidget == null ? void 0 : focusedWidget.id) === id;
  const titleIcon = /* @__PURE__ */ React.createElement(IfDashboard, null, !editing && /* @__PURE__ */ React.createElement(WidgetWarmTierAlert, { widgetId: id, activeQuery }));
  return /* @__PURE__ */ React.createElement(WidgetColorContext, { id }, /* @__PURE__ */ React.createElement(WidgetFrame, { widgetId: id }, /* @__PURE__ */ React.createElement(
    WidgetHeader,
    {
      description: widget.description,
      title,
      titleIcon,
      hideDragHandle: !interactive || isFocused,
      loading,
      editing,
      onRename: onRenameWidget,
      onUpdateDescription
    },
    !editing ? /* @__PURE__ */ React.createElement(
      WidgetActionsMenu,
      {
        isFocused,
        toggleEdit: onToggleEdit,
        title,
        position,
        onPositionsChange
      }
    ) : null
  ), /* @__PURE__ */ React.createElement(
    EditWrapper,
    {
      onToggleEdit,
      onCancelEdit,
      showQueryControls: isDashboard,
      onWidgetConfigChange,
      config,
      editing,
      fields,
      id,
      type: widget.type
    },
    /* @__PURE__ */ React.createElement(WidgetErrorBoundary, null, /* @__PURE__ */ React.createElement(
      Visualization,
      {
        id,
        editing,
        queryId: activeQuery,
        widget,
        fields,
        title,
        setLoadingState: setLoading,
        onToggleEdit,
        onWidgetConfigChange
      }
    ))
  ), /* @__PURE__ */ React.createElement(WidgetFooter, null, interactive ? /* @__PURE__ */ React.createElement("span", null) : /* @__PURE__ */ React.createElement(WidgetDescription, { text: widget.description }), (widget.returnsAllRecords || isDashboard) && !editing && /* @__PURE__ */ React.createElement(
    TimerangeInfo,
    {
      widget,
      activeQuery,
      widgetId: id,
      returnsAllRecords: widget.returnsAllRecords
    }
  ))));
};
export default Widget;
